// Helper utilities for keeping the FCM device token in sync with the server.
// Used by NotificationProvider to detect token refreshes every 6 hours and
// re-register the new token without requiring the user to re-subscribe.

import { getToken, Messaging } from "firebase/messaging";
import { getClientDeviceType, getClientTimeZone } from "@/app/lib/utils";

// localStorage key that remembers the last FCM token we successfully
// registered with the server from this browser/device.
export const LAST_KNOWN_TOKEN_KEY = "fcm_last_known_token";

// Interval at which the client checks whether FCM has rotated the token.
// 6 hours matches the Firebase guidance for opportunistic refresh checks.
export const TOKEN_REFRESH_INTERVAL_MS = 6 * 60 * 60 * 1000;

/**
 * Fetches the current FCM registration token for this device.
 * Returns null on any failure so callers can skip silently.
 */
export async function getCurrentFcmToken(
  messaging: Messaging,
  registration: ServiceWorkerRegistration
): Promise<string | null> {
  try {
    // getToken returns the same token if it has not rotated, or a new one
    // if FCM regenerated it (reinstall, OS update, security refresh, etc.).
    const token = await getToken(messaging, {
      vapidKey: process.env.NEXT_PUBLIC_FIREBASE_VAPID_KEY,
      serviceWorkerRegistration: registration,
    });
    return token || null;
  }
  catch (error) {
    // Best-effort: refresh checks must never crash the app.
    console.warn("[fcmTokenSync] Failed to get current FCM token:", error);
    return null;
  }
}

/**
 * Sends the (possibly refreshed) token to the server's register endpoint.
 * The server is already idempotent — it de-dupes by token base part — so
 * calling this with an unchanged token is safe but wasteful, which is why
 * callers should diff against LAST_KNOWN_TOKEN_KEY first.
 */
export async function syncTokenWithServer(token: string): Promise<boolean> {
  try {
    // Determine the device type ('personal' or 'work') from localStorage.
    // This tells the backend which storage path to use for the token.
    const deviceType = getClientDeviceType();

    // Get the browser's timezone so the backend can calculate the correct
    // expiration time for work device tokens (midnight in the user's timezone).
    // For personal device tokens, this value is ignored by the backend.
    const timeZone = getClientTimeZone();

    const response = await fetch("/api/push-notification/register", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      // Send token, deviceType, and timeZone together so the backend can
      // route to the correct registration logic (personal vs work device).
      body: JSON.stringify({ token, deviceType, timeZone }),
    });
    return response.ok;
  }
  catch (error) {
    console.warn("[fcmTokenSync] Failed to sync token with server:", error);
    return false;
  }
}

/**
 * Runs one refresh check: gets the current token, compares it with what we
 * last registered, and re-syncs only if it has changed. Safe to call on
 * every interval tick and on app mount.
 */
export async function checkAndSyncToken(
  messaging: Messaging,
  registration: ServiceWorkerRegistration
): Promise<void> {
  try {
    // Skip when the user has not granted notification permission. There is
    // nothing meaningful to sync in that case.
    if (typeof Notification === "undefined" || Notification.permission !== "granted") {
      return;
    }

    const currentToken = await getCurrentFcmToken(messaging, registration);
    if (!currentToken) {
      return;
    }

    // Compare against the last token we know the server has for this device.
    const lastKnownToken = typeof window !== "undefined"
      ? window.localStorage.getItem(LAST_KNOWN_TOKEN_KEY)
      : null;

    // No change — server already has this token, skip the network call.
    if (lastKnownToken === currentToken) {
      return;
    }

    // Token has changed (or this is the first check after subscribing).
    // Re-register and remember it on success.
    const ok = await syncTokenWithServer(currentToken);
    if (ok && typeof window !== "undefined") {
      window.localStorage.setItem(LAST_KNOWN_TOKEN_KEY, currentToken);
      console.log("[fcmTokenSync] Token refreshed and re-registered with server");
    }
  }
  catch (error) {
    // Final safety net — never let a refresh check bubble up.
    console.warn("[fcmTokenSync] checkAndSyncToken failed:", error);
  }
}
